import axios, { AxiosError, AxiosResponse } from 'axios';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '@/stores/authStore';

let isRedirecting = false;
let redirectTimeout: NodeJS.Timeout | null = null;

export function installNetworkInterceptor() {
  // Request interceptor para adicionar token de autenticação
  axios.interceptors.request.use(
    (config) => {
      const token = useAuthStore.getState().accessToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // Log da requisição em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.log(`🌐 ${config.method?.toUpperCase()} ${config.url}`, {
          headers: config.headers,
          data: config.data,
        });
      }
      
      return config;
    },
    (error) => {
      console.error('Request interceptor error:', error);
      return Promise.reject(error);
    }
  );

  // Response interceptor para tratar erros de autenticação e outros
  axios.interceptors.response.use(
    (response: AxiosResponse) => {
      // Log da resposta em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ ${response.config.method?.toUpperCase()} ${response.config.url}`, {
          status: response.status,
          data: response.data,
        });
      }
      
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as any;
      const status = error.response?.status;
      const url = error.config?.url;

      // Log do erro
      console.error(`❌ ${error.config?.method?.toUpperCase()} ${url}`, {
        status,
        error: error.message,
        response: error.response?.data,
      });

      // Evita loops de redirecionamento
      if (isRedirecting) {
        console.warn('Redirecionamento já em andamento, ignorando erro de rede');
        return Promise.reject(error);
      }

      // Trata erros de autenticação (401) e autorização (403)
      if ((status === 401 || status === 403) && !originalRequest._retry) {
        originalRequest._retry = true;
        
        // Para 403, tenta renovar o token primeiro
        if (status === 403) {
          const refreshToken = useAuthStore.getState().refreshToken;
          
          if (refreshToken) {
            try {
              console.log('🔄 Token pode ter expirado, tentando renovar...');
              const response = await axios.post('/api/v1/auth/refresh', {
                refreshToken,
              });

              const { accessToken, refreshToken: newRefreshToken } = response.data.data;
              
              // Atualiza tokens no store
              useAuthStore.getState().setTokens(accessToken, newRefreshToken);

              // Repete a requisição original com o novo token
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              console.log('✅ Token renovado, repetindo requisição');
              return axios(originalRequest);
            } catch (refreshError) {
              console.error('❌ Falha ao renovar token:', refreshError);
              // Renovação falhou, faz logout
              handleAuthFailure();
              return Promise.reject(refreshError);
            }
          } else {
            // Sem refresh token, faz logout
            handleAuthFailure();
          }
        } else {
          // Para 401, segue o fluxo normal
          const refreshToken = useAuthStore.getState().refreshToken;
          
          if (refreshToken) {
            try {
              console.log('🔄 Tentando renovar token...');
              const response = await axios.post('/api/v1/auth/refresh', {
                refreshToken,
              });

              const { accessToken, refreshToken: newRefreshToken } = response.data.data;
              
              // Atualiza tokens no store
              useAuthStore.getState().setTokens(accessToken, newRefreshToken);

              // Repete a requisição original com o novo token
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              console.log('✅ Token renovado, repetindo requisição');
              return axios(originalRequest);
            } catch (refreshError) {
              console.error('❌ Falha ao renovar token:', refreshError);
              // Renovação falhou, faz logout
              handleAuthFailure();
              return Promise.reject(refreshError);
            }
          } else {
            // Sem refresh token, faz logout
            handleAuthFailure();
          }
        }
      }

      // Trata outros erros
      handleNetworkError(error, status, url);
      
      return Promise.reject(error);
    }
  );

  console.log('NetworkInterceptor instalado');
}

function handleAuthFailure() {
  if (isRedirecting) return;
  
  isRedirecting = true;
  console.log('🚪 Fazendo logout devido a erro de autenticação');
  
  // Limpa o timeout anterior se existir
  if (redirectTimeout) {
    clearTimeout(redirectTimeout);
  }
  
  // Faz logout
  useAuthStore.getState().logout();
  
  // Redireciona para login com delay para evitar loops
  redirectTimeout = setTimeout(() => {
    const currentPath = window.location.pathname;
    const loginUrl = currentPath.startsWith('/login') ? '/login' : `/login?from=${encodeURIComponent(currentPath)}`;
    
    console.log(`🔄 Redirecionando para: ${loginUrl}`);
    window.location.href = loginUrl;
    
    // Reseta flag após redirecionamento
    setTimeout(() => {
      isRedirecting = false;
    }, 1000);
  }, 100);
}

function handleNetworkError(error: AxiosError, status?: number, url?: string) {
  // Evita mostrar múltiplos toasts para o mesmo erro
  const errorKey = `${status}-${url}`;
  const lastError = (window as any).__lastNetworkError;
  
  if (lastError === errorKey) {
    return;
  }
  
  (window as any).__lastNetworkError = errorKey;
  
  // Remove a chave após 5 segundos para permitir novos toasts
  setTimeout(() => {
    if ((window as any).__lastNetworkError === errorKey) {
      delete (window as any).__lastNetworkError;
    }
  }, 5000);

  let message = 'Erro de conexão';

  switch (status) {
    case 400:
      message = 'Dados inválidos. Verifique as informações enviadas.';
      break;
    case 403:
      message = 'Você não tem permissão para realizar esta ação.';
      break;
    case 404:
      message = 'Recurso não encontrado.';
      break;
    case 409:
      message = 'Conflito de dados. Verifique as informações.';
      break;
    case 422:
      message = 'Dados inválidos. Verifique o formulário.';
      break;
    case 429:
      message = 'Muitas tentativas. Tente novamente em alguns minutos.';
      break;
    case 500:
      message = 'Erro interno do servidor. Tente novamente mais tarde.';
      break;
    case 502:
    case 503:
    case 504:
      message = 'Serviço temporariamente indisponível. Tente novamente.';
      break;
    default:
      if (error.code === 'ECONNABORTED') {
        message = 'Tempo limite excedido. Verifique sua conexão.';
      } else if (!error.response) {
        message = 'Erro de conexão. Verifique sua internet.';
      } else {
        message = `Erro ${status}: ${error.message}`;
      }
  }

  // Adiciona contexto em desenvolvimento
  if (process.env.NODE_ENV === 'development') {
    console.error('Network Error Details:', {
      status,
      url,
      message: error.message,
      response: error.response?.data,
      config: error.config,
    });
  }

  toast.error(message, {
    duration: 5000,
    id: errorKey, // Evita duplicatas
  });
}

// Helper para verificar se há erros de rede ativos
export function hasActiveNetworkErrors(): boolean {
  return isRedirecting;
}

// Helper para limpar estado de redirecionamento (útil para testes)
export function clearRedirectState(): void {
  isRedirecting = false;
  if (redirectTimeout) {
    clearTimeout(redirectTimeout);
    redirectTimeout = null;
  }
}
